import { capitalize } from '@mui/material';
import {
  InvoiceStatus,
  Subscriptions,
  SubscriptionStatus,
} from '@prisma/client';
import type Stripe from 'stripe';
import {
  GetSubscriptionResponse,
  SubscriptionModalContent,
} from '../../types/subscriptions';
import { LoadedUser } from '../../interfaces';
import { isSudoManager } from '../../utils/global/helper';

export function getInvoiceStatusText(status: InvoiceStatus) {
  return capitalize(status.toLowerCase());
}

export function getInvoiceStatusDescription(status: InvoiceStatus) {
  switch (status) {
    case InvoiceStatus.DRAFT:
      return 'This invoice is a draft and will collect payment when finalized.';
    case InvoiceStatus.OPEN:
      return 'This invoice is unpaid.';
    case InvoiceStatus.PAID:
      return 'This invoice is paid.';
    case InvoiceStatus.UNCOLLECTIBLE:
    case InvoiceStatus.VOID:
      return 'This invoice is canceled.';
    default:
      return '';
  }
}

export function getSubscriptionModalContentFromStatus(
  status: SubscriptionStatus,
  canManageSubscription = true,
): SubscriptionModalContent | null {
  switch (status) {
    case SubscriptionStatus.PAUSED:
      return {
        header: 'Trial Expired',
        title: 'Your free trial has ended',
        description: `To continue using Porter, you'll need to subscribe to a monthly plan. ${
          canManageSubscription
            ? 'Subscribe to Porter through the link below to regain access to your account!'
            : 'Please reach out to your shop owner or manager to resubscribe to Porter.'
        }`,
        positiveButtonText: canManageSubscription ? 'Subscribe Now' : '',
        negativeButtonText: canManageSubscription
          ? "I don't want to subscribe"
          : 'Close',
      };
    case SubscriptionStatus.CANCELED:
      return {
        header: 'Subscription Canceled',
        title: 'Your subscription was canceled',
        description: `To continue using Porter, you’ll need to re-subscribe to our monthly plan.${
          canManageSubscription
            ? ''
            : ' Please reach out to your shop owner or manager to resubscribe to Porter.'
        }`,
        positiveButtonText: canManageSubscription ? 'Subscribe Now' : '',
        negativeButtonText: canManageSubscription
          ? "I don't want to subscribe"
          : 'Close',
      };
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.INCOMPLETE:
      return {
        header: 'Payment Failed',
        title: 'Payment for subscription failed',
        description: `The payment for your subscription failed. ${
          canManageSubscription
            ? 'Please update your payment information to continue using the Porter app.'
            : 'Please reach out to your shop owner or manager to update your payment information.'
        }`,
        positiveButtonText: canManageSubscription ? 'Update Payment' : '',
        negativeButtonText: canManageSubscription
          ? "I don't want to subscribe"
          : 'Close',
      };
    default:
      return null;
  }
}

export function getSubscriptionModalContent({
  daysRemaining,
  inFreeTrial,
  paymentCustomer,
  subscription,
  canManageSubscription,
}: {
  paymentCustomer: GetSubscriptionResponse['paymentCustomer'];
  subscription: GetSubscriptionResponse['subscription'];
  daysRemaining: number;
  inFreeTrial: boolean;
  canManageSubscription: boolean;
}): SubscriptionModalContent | null {
  if (!subscription || !paymentCustomer) return null;

  // no modal when user has valid, renewing subscription
  if (
    subscription?.status === SubscriptionStatus.ACTIVE &&
    !!paymentCustomer?.paymentMethods?.length &&
    !subscription.isCanceled
  ) {
    return null;
  }

  if (inFreeTrial) {
    const willRenewTrial =
      !!paymentCustomer?.paymentMethods?.length && !subscription?.isCanceled;

    if (willRenewTrial || (daysRemaining !== 7 && daysRemaining !== 3)) {
      return null;
    }
    const header =
      daysRemaining <= 0
        ? 'Last Day'
        : `${daysRemaining} Day${daysRemaining > 1 ? 's' : ''} Left`;
    return {
      header,
      title: 'Your free trial is about to end',
      description: `Once your free trial ends, you'll no longer be able to use the Porter app.${
        canManageSubscription
          ? ' Subscribe through the link below to keep access to your account!'
          : ' Please reach out to your shop owner or manager to keep your account active.'
      }`,
      positiveButtonText: canManageSubscription ? 'Subscribe Now' : '',
      negativeButtonText: canManageSubscription ? 'Not now' : 'Close',
    };
  }

  return getSubscriptionModalContentFromStatus(
    subscription.status,
    canManageSubscription,
  );
}

export function canUserManageSubscription(user: LoadedUser | null | undefined) {
  return (
    !!user &&
    (user?.business?.type === 'INDEPENDENT' ||
      (user?.business?.type === 'STUDIO' && isSudoManager(user)))
  );
}

export function convertStripeStatus(
  status: Stripe.Subscription.Status,
): SubscriptionStatus {
  switch (status) {
    case 'active':
      return SubscriptionStatus.ACTIVE;
    case 'canceled':
      return SubscriptionStatus.CANCELED;
    case 'incomplete':
      return SubscriptionStatus.INCOMPLETE;
    case 'incomplete_expired':
      return SubscriptionStatus.INCOMPLETE_EXPIRED;
    case 'past_due':
      return SubscriptionStatus.PAST_DUE;
    case 'trialing':
      return SubscriptionStatus.TRIALING;
    case 'unpaid':
      return SubscriptionStatus.UNPAID;
    case 'paused':
      return SubscriptionStatus.PAUSED;
    default:
      return SubscriptionStatus.ACTIVE;
  }
}

export function convertSubscriptionStatus(
  status: SubscriptionStatus,
): Stripe.Subscription.Status {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'active';
    case SubscriptionStatus.CANCELED:
      return 'canceled';
    case SubscriptionStatus.INCOMPLETE:
      return 'incomplete';
    case SubscriptionStatus.INCOMPLETE_EXPIRED:
      return 'incomplete_expired';
    case SubscriptionStatus.PAST_DUE:
      return 'past_due';
    case SubscriptionStatus.TRIALING:
      return 'trialing';
    case SubscriptionStatus.UNPAID:
      return 'unpaid';
    case SubscriptionStatus.PAUSED:
      return 'paused';
    default:
      return 'active';
  }
}

export function isSubscriptionActive(
  subscription: Subscriptions | null | undefined,
) {
  if (!subscription) return false;
  return (['ACTIVE', 'TRIALING'] as SubscriptionStatus[]).includes(
    subscription.status,
  );
}
